import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    NgZone,
    OnDestroy,
    OnInit,
    Renderer2
} from "@angular/core";
import {ScreenLayoutEditorModel} from "../screen-layout-editor.model";
import {ITEM_TYPE, ScreenLayoutMenuData} from "../../../screen-layout.data";
import {Subscription} from "rxjs";
import {ItemDisplayInfo} from "../item-display-info";
import {EditService} from "../../../../../framework/entity/edit/edit-page/edit.service";

/**
 * Menu within a screen layout that manages the touch items
 */
@Component({
    selector: "wbo-screen-layout-menu-bar",
    templateUrl: "./screen-layout-menu-bar.component.html",
    styleUrls: ["./screen-layout-menu-bar.component.scss"]
})
export class ScreenLayoutMenuBarComponent implements OnInit, AfterViewInit, OnDestroy {
    private static readonly HOVER_PAGING_TIME = 500;

    ScreenLayoutEditorModel = ScreenLayoutEditorModel;

    ITEM_TYPE = ITEM_TYPE;

    menus: ScreenLayoutMenuData[] = [];
    page: ScreenLayoutMenuData[] = [];
    menusPerPage: number = 5;
    currentPage: number = 0;
    dragoverNextTimestamp: number;
    dragoverPrevTimestamp: number;
    draggingItem: ScreenLayoutMenuData;
    toggledMenu: ScreenLayoutMenuData;

    private subscriptions: Subscription = new Subscription();

    private tearDownDragOverListenerFn: Function;

    constructor(public model: ScreenLayoutEditorModel,
                protected element: ElementRef,
                protected ngZone: NgZone,
                private changeDetectorRef: ChangeDetectorRef,
                public editService: EditService,
                private renderer: Renderer2) {
    }

    get hasNext(): boolean {
        return this.menus.length > (this.currentPage * this.menusPerPage) + this.menusPerPage;
    }

    get hasPrev(): boolean {
        return this.currentPage > 0;
    }

    ngOnInit(): void {
        this.subscriptions.add(this.model.menus$.subscribe(menus => {
            this.menus = menus;
            this.updatePage();
        }));

        this.subscriptions.add(this.model.selectedMenu$.subscribe(menu => this.findSelected(menu)));

        this.subscriptions.add(this.model.numOfMenus$.subscribe(num => {
            if (!num || num === 0) {
                num = 1;
            }

            if (num > ScreenLayoutEditorModel.MAX_MENUS_PER_PAGE) {
                num = ScreenLayoutEditorModel.MAX_MENUS_PER_PAGE;
            }
            this.menusPerPage = num;
            this.updatePage();
            this.findSelected(this.model.selectedMenu);
        }));
    }

    ngAfterViewInit(): void {
        this.ngZone.runOutsideAngular(() => {
            this.tearDownDragOverListenerFn = this.renderer.listen(this.element.nativeElement,
                "dragover",
                (event: DragEvent) => {
                    const targetItem: ItemDisplayInfo = event["targetItem"];
                    if (!this.draggingItem || !targetItem || targetItem === this.draggingItem) {
                        event.dataTransfer.dropEffect = "none";
                    } else {
                        event.dataTransfer.dropEffect = "none";
                        if (targetItem.itemType === ITEM_TYPE.MENU_BAR_PREV) {
                            this.dragoverPrev(event);
                        } else if (targetItem.itemType === ITEM_TYPE.MENU_BAR_NEXT) {
                            this.dragoverNext(event);
                        } else {
                            event.dataTransfer.dropEffect = "move";
                        }
                    }
                    event.preventDefault();
                    event.stopPropagation();
                });
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();

        this.tearDownDragOverListenerFn();
    }

    updatePage(): void {
        const page: ScreenLayoutMenuData[] = [];
        for (let i = 0; i < this.menusPerPage; i++) {
            const menu: ScreenLayoutMenuData = this.menus[(this.currentPage * this.menusPerPage) + i];
            if (menu) {
                page.push(menu);
            }
        }
        this.page = page;
    }

    /**
     * Navigate through the pages until the selected menu is found
     */
    findSelected(selectedMenu: ScreenLayoutMenuData): void {
        if (!selectedMenu) {
            return;
        }
        this.currentPage = 0;
        this.updatePage();
        while (this.page.indexOf(selectedMenu) === -1 && this.hasNext) {
            this.currentPage++;
            this.updatePage();
        }
    }

    nextPage(): void {
        this.currentPage++;
        this.updatePage();
        if (this.page.indexOf(this.model.selectedMenu) === -1) {
            this.model.selectMenu(this.page[0]);
        }
    }

    prevPage(): void {
        this.currentPage--;
        this.updatePage();
        if (this.page.indexOf(this.model.selectedMenu) === -1) {
            this.model.selectMenu(this.page[0]);
        }
    }

    dragstart(event: DragEvent, item: ScreenLayoutMenuData): void {
        event.dataTransfer.effectAllowed = "move";
        if (this.model.painting || this.editService.entityRestricted) {
            event.preventDefault();
        } else {
            this.draggingItem = item;
            this.model.dragging = true;
        }
    }

    dragend(): void {
        this.draggingItem = undefined;
        this.model.dragging = false;
    }

    drop(event: DragEvent, item: ScreenLayoutMenuData): void {
        const sourceIndex: number = this.menus.indexOf(this.draggingItem);
        const targetIndex: number = this.menus.indexOf(item);
        if (sourceIndex === -1 || targetIndex === -1) {
            return;
        }

        this.menus.splice(sourceIndex < targetIndex ? targetIndex + 1 : targetIndex, 0, this.draggingItem);
        this.menus.splice(sourceIndex < targetIndex ? sourceIndex : sourceIndex + 1, 1);
        this.updatePage();

        this.draggingItem = undefined;
        this.model.dragging = false;
    }

    dragoverPrev(event: DragEvent): void {
        if (!this.dragoverPrevTimestamp) {
            this.dragoverPrevTimestamp = event.timeStamp;
        }
        if (event.timeStamp > this.dragoverPrevTimestamp + ScreenLayoutMenuBarComponent.HOVER_PAGING_TIME) {
            this.dragoverPrevTimestamp = undefined;
            this.prevPage();
            // cancelling the dragging as when moving to another page, the source element that holds the dragend event is destroyed causing it to never detect the end of dragging. So we are
            // cancelling here as it is most used for the dragging image to not have the commands
            this.model.dragging = false;
            this.changeDetectorRef.detectChanges();
        }
    }

    dragleavePrev(): void {
        this.dragoverPrevTimestamp = undefined;
    }

    dragoverNext(event: DragEvent): void {
        if (!this.dragoverNextTimestamp) {
            this.dragoverNextTimestamp = event.timeStamp;
        }
        if (event.timeStamp > this.dragoverNextTimestamp + ScreenLayoutMenuBarComponent.HOVER_PAGING_TIME) {
            this.dragoverNextTimestamp = undefined;
            this.nextPage();
            // cancelling the dragging as when moving to another page, the source element that holds the dragend event is destroyed causing it to never detect the end of dragging. So we are
            // cancelling here as it is most used for the dragging image to not have the commands
            this.model.dragging = false;
            this.changeDetectorRef.detectChanges();
        }
    }

    dragleaveNext(): void {
        this.dragoverNextTimestamp = undefined;
    }

    commandsToggled(toggled: boolean, menu: ScreenLayoutMenuData): void {
        if (toggled) {
            this.toggledMenu = menu;
        }
    }

    navigate(menu: ScreenLayoutMenuData): void {
        this.model.selectMenu(menu, false);
    }
}
